import React, { useState } from 'react';
import { Container } from 'reactstrap';
import { getTokenOrRefresh } from './token_util';
import './custom.css';
import { ResultReason } from 'microsoft-cognitiveservices-speech-sdk';
import { useParams } from 'react-router-dom';

const speechsdk = require('microsoft-cognitiveservices-speech-sdk');

export default function App() {
    const { id } = useParams() ?? 0; // Get the project ID from the URL
    const [displayText, setDisplayText] = useState('INITIALIZED: ready to test speech...');
    const [player, updatePlayer] = useState({ p: undefined, muted: false });

    async function sttFromMic() {
        const tokenObj = await getTokenOrRefresh();
        const speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(tokenObj.authToken, tokenObj.region);
        speechConfig.speechRecognitionLanguage = 'en-US';
        speechConfig.requestWordLevelTimestamps();
        speechConfig.outputFormat = 1;
        const audioConfig = speechsdk.AudioConfig.fromDefaultMicrophoneInput();
        const recognizer = new speechsdk.SpeechRecognizer(speechConfig, audioConfig);

        setDisplayText('speak into your microphone...');

        // Declare mediaRecorder here to trigger microphone settings
        let mediaRecorder;

        // Start both audio capture and speech recognition concurrently
        await Promise.all([
            captureAudioFromMic().then(recorder => {
                mediaRecorder = recorder;
            }),
            new Promise((resolve, reject) => {
                recognizer.recognizeOnceAsync(result => {
                    console.log(result); // Log the result object to inspect its properties
                    if (result.reason === ResultReason.RecognizedSpeech) {
                        setDisplayText(`RECOGNIZED: Text=${result.text} from ${((result.offset * 100)*Math.pow(10, -9)).toFixed(2)}", "duration":"${((result.duration * 100)*Math.pow(10, -9)).toFixed(2)}`);
                        mediaRecorder.stop();
                        mediaRecorder.stream.getTracks().forEach(track => track.stop());
                        resolve();
                    } else {
                        setDisplayText('ERROR: Speech was cancelled or could not be recognized. Ensure your microphone is working properly.');
                        mediaRecorder.stop();
                        reject();
                    }
                });
            })
        ]);
    }

    async function captureAudioFromMic() {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        const audioChunks = [];

        mediaRecorder.ondataavailable = event => {
            audioChunks.push(event.data);
        };

        mediaRecorder.onstop = async () => {
            const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
            await saveAudioToServer(audioBlob);
        };

        mediaRecorder.start();
        return mediaRecorder; // Return the mediaRecorder instance
    }

    async function saveAudioToServer(audioData) {
        const formData = new FormData();
        formData.append('audio', new Blob([audioData], { type: 'audio/wav' }), 'myfile.wav');
        formData.append('id', id); // Use the "id" parameter from the URL

        const response = await fetch('/audioscan.php', {
            method: 'POST',
            body: formData
        });

        if (response.ok) {
            console.log('Audio saved successfully');
        } else {
            console.error('Failed to save audio');
        }
    }

    async function textToSpeech() {
        const tokenObj = await getTokenOrRefresh();
        const speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(tokenObj.authToken, tokenObj.region);
        const myPlayer = new speechsdk.SpeakerAudioDestination();
        updatePlayer(p => { p.p = myPlayer; return p; });
        const audioConfig = speechsdk.AudioConfig.fromSpeakerOutput(player.p);

        let synthesizer = new speechsdk.SpeechSynthesizer(speechConfig, audioConfig);

        const textToSpeak = 'This is an example of speech synthesis for a long passage of text. Pressing the mute button should pause/resume the audio output.';
        setDisplayText(`speaking text: ${textToSpeak}...`);
        synthesizer.speakTextAsync(
            textToSpeak,
            result => {
                let text;
                if (result.reason === speechsdk.ResultReason.SynthesizingAudioCompleted) {
                    text = `synthesis finished for "${textToSpeak}".\n`;
                } else if (result.reason === speechsdk.ResultReason.Canceled) {
                    text = `synthesis failed. Error detail: ${result.errorDetails}.\n`;
                }
                synthesizer.close();
                synthesizer = undefined;
                setDisplayText(text);
            },
            function (err) {
                setDisplayText(`Error: ${err}.\n`);

                synthesizer.close();
                synthesizer = undefined;
            }
        );
    }

    async function handleMute() {
        updatePlayer(p => {
            if (!p.muted) {
                p.p.pause();
                return { p: p.p, muted: true };
            } else {
                p.p.resume();
                return { p: p.p, muted: false };
            }
        });
    }

    async function fileChange(event) {
        const audioFile = event.target.files[0];
        console.log(audioFile);
        const fileInfo = audioFile.name + ` size=${audioFile.size} bytes `;

        setDisplayText(fileInfo);
        const audioConfig = speechsdk.AudioConfig.fromAudioFileInput(audioFile);
        const recognizer = new speechsdk.SpeechRecognizer(speechConfig, audioConfig);

        const tokenObj = await getTokenOrRefresh();
        const speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(tokenObj.authToken, tokenObj.region);
        speechConfig.speechRecognitionLanguage = 'en-US';
        speechConfig.requestWordLevelTimestamps();
        speechConfig.outputFormat = 1;

        recognizer.recognizeOnceAsync(result => {
            let text;
            if (result.reason === ResultReason.RecognizedSpeech) {
                text = `RECOGNIZED: Text=${result.text}`;
            } else {
                text = 'ERROR: Speech was cancelled or could not be recognized. Ensure your microphone is working properly.';
            }

            setDisplayText(fileInfo + text);
        });
    }

    return (
        <Container className="app-container">
            <h1 className="display-4 mb-3">Speech sample app</h1>

            <div className="row main-container">
                <div className="col-6">
                    <i className="fas fa-microphone fa-lg mr-2" onClick={() => sttFromMic()}></i>
                    Convert speech to text from your mic.

                    <div className="mt-2">
                        <label htmlFor="audio-file"><i className="fas fa-file-audio fa-lg mr-2"></i></label>
                        <input
                            type="file"
                            id="audio-file"
                            onChange={(e) => fileChange(e)}
                            style={{ display: "none" }}
                        />
                        Convert speech to text from an audio file.
                    </div>
                    <div className="mt-2">
                        <i className="fas fa-volume-up fa-lg mr-2" onClick={() => textToSpeech()}></i>
                        Convert text to speech.
                    </div>
                    <div className="mt-2">
                        <i className="fas fa-volume-mute fa-lg mr-2" onClick={() => handleMute()}></i>
                        Pause/resume text to speech output.
                    </div>

                </div>
                <div className="col-6 output-display rounded">
                    <code>{displayText}</code>
                </div>
            </div>
        </Container>
    );
}